/**
 * Pure URL builders for entries this editor mints — an admin-authored pack/catalog
 * lives at the standard docs/168/343 gated path (`<id>/manifest.json`/
 * `<id>/catalog.json`), so an entry the editor creates via its "pick one of my own
 * saved packs/catalogs" flow can auto-compute a valid `manifestUrl`/`url` instead
 * of asking the admin to type it (docs/343 §3). `ownedXIdFromUrl` is the exact
 * inverse — used to tell whether an EXISTING entry (loaded from a saved catalog,
 * possibly hand-edited) still points at one of this admin's own documents, so the
 * tree can offer a "Open pack/catalog →" link / nest it as a real subtree.
 */

export function gatedManifestUrl(backendUrl: string, packId: string): string {
  return `${backendUrl.replace(/\/+$/, "")}/gated/${encodeURIComponent(packId)}/manifest.json`;
}

export function gatedCatalogUrl(backendUrl: string, catalogId: string): string {
  return `${backendUrl.replace(/\/+$/, "")}/gated/${encodeURIComponent(catalogId)}/catalog.json`;
}

function ownedIdFromUrl(backendUrl: string, url: string | undefined, filename: string): string | null {
  if (!url) return null;
  const base = backendUrl.replace(/\/+$/, "");
  const prefix = `${base}/gated/`;
  const suffix = `/${filename}`;
  if (!url.startsWith(prefix) || !url.endsWith(suffix)) return null;
  const middle = url.slice(prefix.length, url.length - suffix.length);
  if (!middle || middle.includes("/")) return null;
  try {
    return decodeURIComponent(middle);
  } catch {
    return null;
  }
}

/** The pack id, IFF `url` is exactly this backend's own `<id>/manifest.json` gated path. */
export function ownedPackIdFromUrl(backendUrl: string, url: string | undefined): string | null {
  return ownedIdFromUrl(backendUrl, url, "manifest.json");
}

/** The catalog id, IFF `url` is exactly this backend's own `<id>/catalog.json` gated path. */
export function ownedCatalogIdFromUrl(backendUrl: string, url: string | undefined): string | null {
  return ownedIdFromUrl(backendUrl, url, "catalog.json");
}
